import React, { useCallback } from 'react';
import { Flex, Box } from 'rebass';
import LayerRenderer from './LayerRenderer';
import { usePageContext } from 'collections/book-creation/PageContext';
import { checkIsIpadUser } from 'story-media/hooks';
import MediaSelectionOverlay from '../MediaSelectionOverlay';
import PageBoundaryGuide from '../PageBoundaryGuide';

// `layerEditor` (see hooks/usePageLayerEditor) is only ever passed for the
// one page the member is actively editing — every other call site of
// SliderPageRenderer (thumbnails, layout-possibility review, the read-only
// Talking Family Tree preview) simply doesn't pass it, so this is a no-op
// there: no position overrides, no selection overlays.
const LayeredData = React.memo(function LayeredData({
    pageLayers,
    zoom,
    isFromPossibilityReview,
    selectedLayer,
    attributes,
    layerEditor,
    getPositionOverride,
    ...props
}) {

    if (!pageLayers || pageLayers.length === 0) return <Box />;

    return (
        <>
            {pageLayers.map(({ id, ...layer }) => {
                // Only override when the layer has actually been moved/resized —
                // see getOverrideIfDirty/getOverrideForPage. Layers that are
                // rendered via LayerRenderer's own useEntity lookup (the common
                // case: a standalone top-level image/text layer) otherwise
                // completely ignore this `layer` object for position, so
                // `positionOverride` is threaded through explicitly to reach
                // them. getPositionOverride (bound to THIS page, regardless of
                // whether it's the one currently open for interaction) is the
                // general case; layerEditor's own getOverrideIfDirty is kept as
                // a fallback for any caller that doesn't pass the former.
                const override = getPositionOverride?.(id) ?? layerEditor?.getOverrideIfDirty?.(id);
                const effectiveLayer = override
                    ? {
                        ...layer,
                        ...override,
                        'relative-xpos': override.xpos,
                        'relative-ypos': override.ypos,
                    }
                    : layer;

                return (
                    <Box
                        key={id}
                        id={id}
                        sx={
                            layer.children?.length > 0
                                ? {
                                    position: props?.renderedAsChild
                                        ? 'relative'
                                        : 'absolute',
                                    top: `${effectiveLayer.ypos}px`,
                                    left: `${effectiveLayer.xpos}px`,
                                }
                                : undefined
                        }
                    >
                        <RenderLayer
                            layer={{ id, ...effectiveLayer }}
                            zoom={zoom}
                            isFromPossibilityReview={isFromPossibilityReview}
                            selectedLayer={selectedLayer}
                            attributes={attributes}
                            positionOverride={override}
                            {...props}
                        />
                    </Box>
                );
            })}
            {layerEditor && layerEditor.selectedLayerIds.length > 0 && !layerEditor.isLocked && (
                <PageBoundaryGuide pageWidth={layerEditor.pageWidth} pageHeight={layerEditor.pageHeight} />
            )}
            {layerEditor && layerEditor.groups.map(group =>
                <MediaSelectionOverlay
                    key={group.key}
                    group={group}
                    isSelected={group.layerIds.some(id => layerEditor.selectedLayerIds.includes(id))}
                    isLocked={layerEditor.isLocked}
                    layerEditor={layerEditor}
                />
            )}
        </>
    );
});

// ============================================================
// RenderLayer — module-level component, never recreated on render
// ============================================================
const RenderLayer = React.memo(function RenderLayer({
    layer,
    zoom,
    isFromPossibilityReview,
    selectedLayer,
    attributes,
    positionOverride,
    ...props
}) {
    if (!layer.enabled) {
        if (layer.children && layer.children.length > 0) {
            return (
                <LayerRenderer
                    isFromPossibilityReview={isFromPossibilityReview}
                    {...props}
                    id={layer.id}
                    type={props?.type || "book-page-layers"}
                    zoom={zoom}
                    hidePositionCoordinates
                    addID="ID FROM DISABLED"
                    selectedLayer={selectedLayer}
                    pageId={attributes?.id}
                >
                    <Flex
                        id={layer.id}
                        sx={{
                            width: `${layer.width}px`,
                            height: `${layer.height}px`,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {layer.children.map((child) => (
                            <RenderLayer
                                key={child.id}
                                layer={child}
                                zoom={zoom}
                                isFromPossibilityReview={isFromPossibilityReview}
                                selectedLayer={selectedLayer}
                                attributes={attributes}
                                {...props}
                            />
                        ))}
                    </Flex>
                </LayerRenderer>
            );
        }
        return null;
    }

    const enabledChildren = layer.children || [];

    if (enabledChildren.length > 0) {
        return (
            <LayerRenderer
                isFromPossibilityReview={isFromPossibilityReview}
                {...props}
                type={props?.type || "book-page-layers"}
                id={layer.id}
                zoom={zoom}
                hidePositionCoordinates
                addID="ID FROM ENABLED"
                selectedLayer={selectedLayer}
                pageId={attributes?.id}
            >
                <Flex
                    id={layer.id}
                    sx={{
                        position: 'absolute',
                        left: `${layer.xpos}px`,
                        top: `${layer.ypos}px`,
                        width: `${layer.width}px`,
                        height: `${layer.height}px`,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {enabledChildren.map((child) => (
                        <RenderLayer
                            key={child.id}
                            layer={child}
                            zoom={zoom}
                            isFromPossibilityReview={isFromPossibilityReview}
                            selectedLayer={selectedLayer}
                            attributes={attributes}
                            {...props}
                        />
                    ))}
                </Flex>
            </LayerRenderer>
        );
    }

    return (
        <LayerRenderer
            isFromPossibilityReview={isFromPossibilityReview}
            {...props}
            id={layer.id}
            type={props?.type || "book-page-layers"}
            zoom={zoom}
            hidePositionCoordinates
            addID="NORMAL ONE"
            selectedLayer={selectedLayer}
            pageId={attributes?.id}
            positionOverride={positionOverride}
        />
    );
});

// ============================================================
// SliderPageRenderer — memoized outer component
// ============================================================
const SliderPageRenderer = React.memo(function SliderPageRenderer({
    attributes,
    zoom = 4.6,
    isFromSlider = false,
    isFromPossibilityReview,
    selectedLayer,
    relationships,
    isSelected,
    index,
    layers,
    setCurrentPage,
    isReadOnly,
    layerEditor,
    ...props
}) {
    const pageLayers = layers;
    // const { setCurrentPage } = usePageContext ? usePageContext() : {};
    // Clicking empty page area (not a media group) clears the active selection.
    //
    // setCurrentPage expects the full transformed page shape — { id,
    // pageName, kind, attributes, layers }, see helper.js's transformAllPages
    // — not the bare `attributes` sub-object. Passing `attributes` alone
    // silently dropped `id`/`layers`, which usePageLayerEditor depends on to
    // know which page it's editing; nothing noticed before because nothing
    // else read `currentPage.layers` directly. Reconstructed here from props
    // (id/pageName/kind arrive via PageFace's `{...page}` spread).
    const handleClick = useCallback(() => {
        if (isFromPossibilityReview) return;

        layerEditor?.clearSelection?.();
        if (setCurrentPage) {
            setCurrentPage({
                id: props?.id,
                pageName: props?.pageName,
                kind: props?.kind,
                attributes,
                layers: pageLayers,
            });
        }
    }, [setCurrentPage, attributes, layerEditor, pageLayers, props?.id, props?.pageName, props?.kind]);

    const isIpadUser = checkIsIpadUser();

    // Scaling method. CSS `zoom` re-runs layout at the scaled size, so tiny scaled
    // font sizes get rounded/floored differently by Safari vs Chrome — the same text
    // wraps to one line in Chrome but three in Safari. `transform: scale()` lays the
    // page out at its NATIVE size (identical text metrics in every engine) and only
    // scales the rendered result, so wrapping matches across browsers. Gated to the
    // TFT read-only preview to avoid touching the editable book's zoom-coordinate math.
    const scaleSx = (props?.isFromTFT && !isIpadUser)
        ? {
            transform: `scale(${zoom / 100})`,
            transformOrigin: '0 0',
        }
        : {
            zoom: `${zoom}%`,
            MozTransform: `scale(${zoom / 100})`,
            MozTransformOrigin: '10% 0',
        };

    if (props?.isEmpty)
        return <Box
            height={props?.height}
            width={props?.width}
            sx={{
                bg: attributes?.['background-colour'],
                boxShadow: isFromPossibilityReview
                    ? '0 1.626px 3.253px 0.813px rgba(0, 0, 0, 0.15)'
                    : index % 2 === 0
                        ? '-20px 0 40px rgba(0, 0, 0, 0.25), 0 30px 50px rgba(0, 0, 0, 0.15)'
                        : '20px 0 40px rgba(0, 0, 0, 0.25), 0 30px 50px rgba(0, 0, 0, 0.15)',
                borderRadius: isFromPossibilityReview
                    ? '38.176px'
                    : index % 2 === 0
                        ? '67.16px 0 0 67.16px'
                        : '0 67.16px 67.16px 0',
            }}

        />
    if (!pageLayers || pageLayers.length === 0) return null;

    return (<>
        {/* {(isFromSlider && !isSelected) && <Box
            onClick={handleClick}
            sx={{
                zoom: `${zoom}%`,
                MozTransform: `scale(${zoom / 100})`,
                MozTransformOrigin: '10% 0',
                position: 'absolute',
                width: `${attributes?.width}px`,
                height: `${attributes?.height}px`,
                left: 0,
                right: 0,
                m: 'unset',
                background: 'rgba(52, 73, 94, 0.5)',
                zIndex: 9999
            }}
        />} */}
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            onClick={handleClick}
            sx={{
                ...scaleSx,
                position: 'relative',

                boxShadow: isFromPossibilityReview
                    ? '0 1.626px 3.253px 0.813px rgba(0, 0, 0, 0.15)'
                    : index % 2 === 0
                        ? '-20px 0 40px rgba(0, 0, 0, 0.25), 0 30px 50px rgba(0, 0, 0, 0.15)'
                        : '20px 0 40px rgba(0, 0, 0, 0.25), 0 30px 50px rgba(0, 0, 0, 0.15)',
                borderRadius: isFromPossibilityReview
                    ? '38.176px'
                    : index % 2 === 0
                        ? '67.16px 0 0 67.16px'
                        : '0 67.16px 67.16px 0',
                width: `${attributes?.width}px`,
                height: `${attributes?.height}px`,
                left: 0,
                right: 0,
                m: 'unset',
                // border:
                //     !isFromSlider && isSelected
                //         ? '33px solid #C470E6'
                //         : undefined,
                touchAction: 'pan-x pan-y pinch-zoom',
                bg: attributes?.['background-colour'],
                ...(isFromSlider && {
                    zIndex: isSelected ? 999 : undefined,
                    opacity: 1,
                }),
                ...(isFromPossibilityReview && {
                    '&:hover': { border: '30.365px solid #9B59B6' },
                }),
            }}
            id={'templateContainer ' + props?.id}
        >
            <LayeredData
                pageLayers={pageLayers}
                isFromPossibilityReview={isFromPossibilityReview}
                selectedLayer={selectedLayer}
                zoom={zoom}
                attributes={attributes}
                layerEditor={layerEditor}
                {...props}
            />
        </Flex>
    </>
    );
});

export default SliderPageRenderer;
